import { useMemo, useState, useRef, useLayoutEffect, type CSSProperties, type PointerEvent as RPointerEvent, type KeyboardEvent as RKeyboardEvent } from 'react';
import { useApp } from '../store';
import type { Issue } from '../types';
import { livePhases } from '../phases';
import { isProjectMode } from '../lifecycle';
import { isWorkingDay, holidayOn, type WorkCalendar } from '../work-calendar';
import { criticalPath, type CpmNode } from '../critical-path';
import { predecessorsOf, type DepCard } from '../dependencies';
import { pacePicture } from '../pace';
import { effectiveProgressPct } from '../progress-derive';
import { displayName } from '../people';
import { initials, asDate } from '../utils';
import { MONTHS } from '../when';

/**
 * THE CALENDAR — the third board mode (beside Board and Grid). Every board gets it:
 * a continuous board opens on the MONTH grid (cards sit on their dates). A project
 * adds the Gantt TIMELINE with the CPM critical path drawn in brand petrol, and the
 * Timeline ↔ Month switch to move between them.
 *
 * One bar per card, planned start → due, laid on a dated canvas whose weekends and
 * workspace holidays are shaded and skipped (work-calendar.ts). The brand petrol is
 * spent on the ONE thing that matters — the zero-float chain that sets the finish
 * date (critical-path.ts) — never red, which stays the RAG alarm (the overdue flag).
 * Float is measured against the FROZEN committed go-live (project.goLive); the
 * forecast is the shared pace read (pace.ts), drawn as its own tracking line so the
 * baseline and the forecast are never collapsed.
 *
 * Honest coverage couples the schedule to data quality: a card with no start/due has
 * no window to place, so it is left OUT of the critical-path subgraph, named in the
 * foot, and parked in the Unscheduled tray with a one-click "Set dates" — the same
 * cards Layer A's Needs Attention flags.
 *
 * (This slice: Timeline + the mode/group/zoom controls + coverage + tray. The Month
 * grid, the dependency-arrow overlay and the go-live milestone diamonds land in the
 * next slice.)
 */

const DAY = 86_400_000;
const GROUP_H = 30;
const ROW_H = 32;
const BAR_H = 20; // the bar's height; a bar centres in its (possibly taller, wrapped) row
                 // → barTop = rowTop + (rowH − BAR_H)/2, which is the old inset 6 at 32px
// MIN density per zoom — the natural px-per-day. A project wider than the monitor keeps
// this density and scrolls (no regression). The canvas fills a wide monitor by GROWING
// dayPx from here up to MAX; past MAX a short project would balloon into meaningless fat
// columns, so it stops and the panel hugs its content instead (Vinnie, 2 Nov 2026).
const DAY_PX: Record<Zoom, number> = { day: 30, week: 13, month: 6 };
const MAX_PX: Record<Zoom, number> = { day: 44, week: 20, month: 10 };
const LABEL_W = 236; // the fixed label column; the canvas gets the rest of the width
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
// The connector colour by its state — petrol on the critical chain, amber where an
// overdue card is holding up the next, green once cleared, grey for a plain pending.
const LINK_STROKE: Record<string, string> = {
  critical: 'var(--brand-solid)',
  'at-risk': 'var(--s-inprogress)',
  cleared: 'var(--s-done)',
  pending: 'var(--t3)',
};

type Group = 'phase' | 'epic';
type Zoom = 'day' | 'week' | 'month';
type CalView = 'timeline' | 'month';
const MONTH_LANE_H = 24; // vertical pitch of one event lane in the month grid (19px bar + 5px gap)
const MONTH_LANE_CAP = 6; // a comfortable week height — lanes past this collapse behind "+N more" (Apple/Google-style)

const hasDay = (d: string | null | undefined): d is string => typeof d === 'string' && d.length > 0;
const isResolved = (s: Issue['status']) => s === 'done' || s === 'wontdo';

/** Local-midnight ms for a YYYY-MM-DD (matches work-calendar's day handling). */
function localMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
}
/** An ms instant back to its local YYYY-MM-DD. */
function toISO(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** Whole calendar days from a to b (b − a). */
function daysBetween(aISO: string, bISO: string): number {
  return Math.round((localMs(bISO) - localMs(aISO)) / DAY);
}

interface RowCard {
  kind: 'card';
  card: Issue;
  node: CpmNode | undefined;
  top: number; // the row's y within g-body
}
interface RowGroup {
  kind: 'group';
  name: string;
  count: number;
  note: string;
  top: number;
}
type Row = RowGroup | RowCard;

/** The axis uses the app's one worded-month list (R16), so Sep reads "Sept" here
 *  exactly as it does everywhere else. */


export default function CalendarView() {
  const issues = useApp((s) => s.issues);
  const phases = useApp((s) => s.phases);
  const workCalendar = useApp((s) => s.workCalendar) as WorkCalendar;
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const profiles = useApp((s) => s.profiles);
  const openIssue = useApp((s) => s.openIssue);
  const meta = useApp((s) => s.meta);

  // The Gantt Timeline (bars, the critical path, the go-live baseline) is the
  // project-only half of Calendar; the Month grid is for every board. A continuous
  // board therefore opens straight on Month and never offers the Timeline — #508
  // follow-up (Vinnie 31 Oct: "month view should be available in all boards").
  const isProject = isProjectMode(meta);

  const [group, setGroup] = useState<Group>('phase');
  const [view, setView] = useState<CalView>(isProject ? 'timeline' : 'month');
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  // Which overflowing weeks the person has expanded, keyed by week-start ISO (stable
  // across months, so stepping away and back keeps each week's own state).
  const [openWeeks, setOpenWeeks] = useState<Set<string>>(new Set());
  const toggleWeek = (key: string) => setOpenWeeks((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const project = projects.find((p) => p.id === projectId);
  const goLiveISO = project?.goLive ? toISO(project.goLive) : null;
  const now = Date.now();
  const todayISO = toISO(now);

  // The Timeline FILLS the monitor, like the board (Vinnie, 2 Nov 2026). The canvas
  // can't do this in pure CSS — the bars are absolutely positioned off a px-per-day
  // scale — so we MEASURE the available width (the full-width .calview, minus the label
  // column and the panel border) and compute the scale from it. The measure runs in a
  // layout effect so the first paint already fills (no R32 pop); a window-resize reflow
  // then tracks the window continuously, like the other 4.0.4 width-tier views.
  const calRef = useRef<HTMLDivElement>(null);
  const [availW, setAvailW] = useState(0);
  useLayoutEffect(() => {
    const el = calRef.current;
    if (!el) return;
    const measure = () => setAvailW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The label column is DRAGGABLE — a person can trade canvas width for more room to read
  // long titles (on top of the two-line wrap). The chosen width is remembered per board.
  const labelKey = `fd.gantt.labelW.${projectId}`;
  const [labelW, setLabelW] = useState<number>(() => {
    try { const v = localStorage.getItem(labelKey); if (v) return clamp(+v, 160, 560); } catch { /* private mode */ }
    return LABEL_W;
  });
  useLayoutEffect(() => {
    try { const v = localStorage.getItem(labelKey); setLabelW(v ? clamp(+v, 160, 560) : LABEL_W); } catch { setLabelW(LABEL_W); }
  }, [labelKey]);
  const splitDrag = useRef<{ x: number; w: number } | null>(null);
  const onSplitDown = (e: RPointerEvent) => {
    e.preventDefault();
    splitDrag.current = { x: e.clientX, w: labelW };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onSplitMove = (e: RPointerEvent) => {
    if (!splitDrag.current) return;
    const maxW = Math.max(200, Math.min(560, (availW || 1200) * 0.5));
    setLabelW(clamp(splitDrag.current.w + (e.clientX - splitDrag.current.x), 160, maxW));
  };
  const onSplitUp = (e: RPointerEvent) => {
    if (!splitDrag.current) return;
    splitDrag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    try { localStorage.setItem(labelKey, String(Math.round(labelW))); } catch { /* private mode */ }
  };
  // Keyboard: focus the divider and nudge it with the arrow keys (a11y — the divider is a
  // role="separator", so it should be operable without a pointer).
  const onSplitKey = (e: RKeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const maxW = Math.max(200, Math.min(560, (availW || 1200) * 0.5));
    const next = clamp(labelW + (e.key === 'ArrowRight' ? 16 : -16), 160, maxW);
    setLabelW(next);
    try { localStorage.setItem(labelKey, String(Math.round(next))); } catch { /* private mode */ }
  };

  // The width the dated canvas has to work with. Before the first measure (availW 0) we
  // fall back to the old fixed target so a pre-paint frame never looks broken.
  const canvasAvail = availW > 0 ? Math.max(320, availW - labelW - 2) : 1000;

  // Default the zoom so the WHOLE project frames in view — the critical path should be
  // legible without scrolling. The reviewer can still pick Day/Week/Month to override it.
  // The default is the finest UNIT whose whole span fits the MEASURED width at its natural
  // density (so it fills without scrolling); a picked zoom still sticks. The scale WITHIN
  // the unit is then set by the fill (see the model), so a wide monitor can promote the
  // default from Month to Week for free.
  const fitZoom = useMemo<Zoom>(() => {
    const ms: number[] = [];
    for (const i of issues) {
      if (i.status === 'wontdo') continue;
      if (hasDay(i.startsOn)) ms.push(localMs(i.startsOn!));
      if (hasDay(i.dueDate)) ms.push(localMs(i.dueDate!));
    }
    if (goLiveISO) ms.push(localMs(goLiveISO));
    ms.push(now);
    if (ms.length < 2) return 'week';
    const spanDays = (Math.max(...ms) - Math.min(...ms)) / DAY + 12;
    for (const z of ['day', 'week', 'month'] as Zoom[]) if (spanDays * DAY_PX[z] <= canvasAvail) return z;
    return 'month';
  }, [issues, goLiveISO, now, canvasAvail]);
  const [zoomOverride, setZoomOverride] = useState<Zoom | undefined>(undefined);
  const zoom = zoomOverride ?? fitZoom;

  const model = useMemo(() => {
    const live = issues.filter((i) => i.status !== 'wontdo');
    const scheduled = live.filter((i) => hasDay(i.startsOn) && hasDay(i.dueDate));
    const undated = live.filter((i) => !(hasDay(i.startsOn) && hasDay(i.dueDate)));

    // CPM over every non-cancelled card (dated ones placed, undated reported in
    // coverage). Float is measured against the frozen committed go-live.
    const depCards: DepCard[] = live.map((i) => ({
      id: i.id, ref: i.ref, status: i.status, startsOn: i.startsOn, dueDate: i.dueDate, blockerRefs: i.blockerRefs,
    }));
    const cpm = criticalPath(depCards, goLiveISO, workCalendar);

    // The shared forecast finish (pace.ts) — the tracking line, never the baseline.
    const pace = pacePicture(issues, issues.filter((i) => !isResolved(i.status)), now, goLiveISO ?? undefined, workCalendar);
    const forecastISO = pace.p85Ms ? toISO(pace.p85Ms) : null;

    // The time domain: a little before the earliest start, a little past the latest
    // of the due dates / go-live / forecast / today, so every line stays in view.
    const days = [
      ...scheduled.map((i) => i.startsOn!),
      ...scheduled.map((i) => i.dueDate!),
      todayISO,
      ...(goLiveISO ? [goLiveISO] : []),
      ...(forecastISO ? [forecastISO] : []),
    ];
    const minISO = days.reduce((m, d) => (d < m ? d : m), days[0] ?? todayISO);
    const maxISO = days.reduce((m, d) => (d > m ? d : m), days[0] ?? todayISO);
    const domainStart = toISO(localMs(minISO) - 3 * DAY);
    const domainEnd = toISO(localMs(maxISO) + 3 * DAY);
    const span = Math.max(1, daysBetween(domainStart, domainEnd));
    // FILL: grow the scale from the natural density up to the measured width, capped so a
    // short project doesn't balloon. A long project lands below MIN → stays at MIN and
    // scrolls. When even MAX can't fill, the canvas is narrower than the panel and the
    // panel hugs it (CSS .gantt { width: max-content }), so no half-empty box.
    const dayPx = clamp(canvasAvail / span, DAY_PX[zoom], MAX_PX[zoom]);
    const width = span * dayPx;
    const x = (iso: string) => daysBetween(domainStart, iso) * dayPx;

    // Grouping — phases in their plan order (then a trailing "no phase"), or epics.
    const buckets: { key: string; name: string; note: string; cards: Issue[] }[] = [];
    if (group === 'phase') {
      const ordered = livePhases(phases);
      for (const ph of ordered) {
        const cards = scheduled.filter((c) => c.phase === ph.id);
        if (cards.length) buckets.push({ key: ph.id, name: ph.name, note: '', cards });
      }
      const noPhase = scheduled.filter((c) => !c.phase || !ordered.some((p) => p.id === c.phase));
      if (noPhase.length) buckets.push({ key: '(none)', name: 'No phase', note: '', cards: noPhase });
    } else {
      const byEpic = new Map<string, Issue[]>();
      for (const c of scheduled) {
        const k = hasDay(c.epic) ? c.epic! : '(none)';
        (byEpic.get(k) ?? byEpic.set(k, []).get(k)!).push(c);
      }
      for (const [k, cards] of [...byEpic.entries()].sort((a, b) => (a[0] === '(none)' ? 1 : b[0] === '(none)' ? -1 : a[0] < b[0] ? -1 : 1))) {
        buckets.push({ key: k, name: k === '(none)' ? 'No epic' : k, note: '', cards });
      }
    }

    // Flatten to rows with PROVISIONAL cumulative y offsets (one line per card). These
    // are the first-paint fallback; the real tops come from MEASURING the label rows once
    // they render, because a long title now WRAPS to two lines and grows its row (and the
    // canvas lane with it). rowOfRef lets the links/floats address a card by its row index
    // so their Y can be read off the measured heights at render time.
    const rows: Row[] = [];
    const rowOfRef = new Map<string, number>();
    let y = 0;
    for (const b of buckets) {
      const openN = b.cards.filter((c) => c.status === 'inprogress' || c.status === 'staging').length;
      const doneN = b.cards.filter((c) => c.status === 'done').length;
      const note = doneN === b.cards.length ? 'done' : openN > 0 ? 'in progress' : 'not started';
      rows.push({ kind: 'group', name: b.name, count: b.cards.length, note, top: y });
      y += GROUP_H;
      for (const card of [...b.cards].sort((a, c) => (a.startsOn! < c.startsOn! ? -1 : a.startsOn! > c.startsOn! ? 1 : 0))) {
        rowOfRef.set(card.ref, rows.length);
        rows.push({ kind: 'card', card, node: cpm.nodes.get(card.ref), top: y });
        y += ROW_H;
      }
    }
    const bodyH = y;

    // DEPENDENCY LINKS — a finish→start connector from each predecessor's bar end to
    // its successor's bar start, so you can follow what waits on what. A link whose
    // BOTH ends are on the zero-float chain is drawn in brand petrol (the critical
    // path); every other link is a muted grey. And a FLOAT tail on each card that can
    // slip — from its due date out to its latest finish — so the slack is visible too.
    // Horizontal geometry only (x from the day scale); the vertical (yc) is resolved at
    // render from the measured row heights, so it is carried as a row INDEX, not a px y.
    const geom = new Map<string, { x1: number; x2: number; row: number }>();
    for (const r of rows) {
      if (r.kind !== 'card') continue;
      const c = r.card;
      const left = x(c.startsOn!);
      const w = Math.max(dayPx, x(c.dueDate!) + dayPx - left);
      geom.set(c.ref, { x1: left, x2: left + w, row: rowOfRef.get(c.ref)! });
    }
    const cardByRef = new Map(scheduled.map((c) => [c.ref, c]));
    type LinkState = 'critical' | 'at-risk' | 'cleared' | 'pending';
    const links: { x1: number; x2: number; fromRow: number; toRow: number; state: LinkState; fromRef: string; toRef: string }[] = [];
    const floats: { left: number; width: number; row: number; slack: number }[] = [];
    for (const r of rows) {
      if (r.kind !== 'card') continue;
      const c = r.card;
      const g = geom.get(c.ref);
      if (!g) continue;
      for (const pr of predecessorsOf({ id: c.id, ref: c.ref, status: c.status, startsOn: c.startsOn, dueDate: c.dueDate, blockerRefs: c.blockerRefs })) {
        const pg = geom.get(pr);
        if (!pg) continue;
        // The link's state reads off the PREDECESSOR: done = cleared, overdue-and-
        // still-open = a live slip (at-risk), otherwise petrol if on the critical
        // chain, else a neutral pending grey.
        const pred = cardByRef.get(pr);
        const crit = !!(cpm.nodes.get(pr)?.critical && r.node?.critical);
        const state: LinkState =
          pred?.status === 'done' ? 'cleared'
          : pred && !isResolved(pred.status) && hasDay(pred.dueDate) && pred.dueDate! < todayISO ? 'at-risk'
          : crit ? 'critical'
          : 'pending';
        links.push({ x1: pg.x2, x2: g.x1, fromRow: pg.row, toRow: g.row, state, fromRef: pr, toRef: c.ref });
      }
      const n = r.node;
      // Slack is room left to slip BEFORE finishing — a finished card has none to
      // offer, so the float band never shows on a done/won't-do row (it read as
      // "this could still slip" on work that was already over — CSM witness, 3 Nov).
      if (n && !isResolved(c.status) && !n.critical && n.totalFloatWorkingDays > 0 && n.latestFinish > c.dueDate!) {
        const fl = x(c.dueDate!) + dayPx;
        const fr = x(n.latestFinish) + dayPx;
        if (fr > fl) floats.push({ left: fl, width: fr - fl, row: g.row, slack: n.totalFloatWorkingDays });
      }
    }

    // Adjacency, so hovering a card can light its whole up+down chain.
    const succOf = new Map<string, string[]>();
    const predOf = new Map<string, string[]>();
    for (const l of links) {
      (succOf.get(l.fromRef) ?? succOf.set(l.fromRef, []).get(l.fromRef)!).push(l.toRef);
      (predOf.get(l.toRef) ?? predOf.set(l.toRef, []).get(l.toRef)!).push(l.fromRef);
    }

    // The weekend / holiday day-bands across the whole domain.
    const bands: { left: number; width: number; holiday: boolean; name?: string }[] = [];
    for (let d = 0; d < span; d++) {
      const iso = toISO(localMs(domainStart) + d * DAY);
      const hol = holidayOn(iso, workCalendar);
      if (!isWorkingDay(iso, workCalendar)) bands.push({ left: d * dayPx, width: dayPx, holiday: !!hol, name: hol });
    }

    // Axis ticks: a label at the first of each month, a week number on each Monday.
    const months: { left: number; label: string }[] = [];
    const weeks: { left: number; label: string }[] = [];
    for (let d = 0; d <= span; d++) {
      const ms = localMs(domainStart) + d * DAY;
      const date = new Date(ms);
      if (date.getDate() === 1) months.push({ left: d * dayPx, label: date.getMonth() === 0 ? `${MONTHS[0]} ${date.getFullYear()}` : MONTHS[date.getMonth()] });
      if (date.getDay() === 1) weeks.push({ left: d * dayPx, label: String(date.getDate()) });
    }

    return { cpm, rows, bodyH, bands, months, weeks, width, x, dayPx, links, floats, succOf, predOf, undated, forecastISO, scheduledCount: scheduled.length, totalCount: live.length };
  }, [issues, phases, workCalendar, goLiveISO, group, zoom, now, todayISO, canvasAvail]);

  const { cpm, rows, bodyH, bands, months, weeks, width, x, links, floats, succOf, predOf, undated, forecastISO } = model;

  // MEASURE the label rows so a wrapped two-line title grows BOTH the label row and the
  // canvas lane in lockstep (bar↔label alignment stays pixel-exact). The canvas can't know
  // a title's rendered height, so we read it from the DOM in a layout effect (before paint,
  // no pop) and drive every vertical position off it. The equality guards stop a re-measure
  // from looping. Deps: the rows (titles) and canvasAvail (a resize can rewrap at the edge).
  const labelsRef = useRef<HTMLDivElement>(null);
  const [tops, setTops] = useState<number[]>([]);
  const [heights, setHeights] = useState<number[]>([]);
  const [measuredBodyH, setMeasuredBodyH] = useState(0);
  const eqArr = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);
  useLayoutEffect(() => {
    const el = labelsRef.current;
    if (!el) return;
    const kids = Array.from(el.children).filter(
      (c) => c.classList.contains('g-group') || c.classList.contains('g-lrow'),
    ) as HTMLElement[];
    if (!kids.length) { setTops((p) => (p.length ? [] : p)); setHeights((p) => (p.length ? [] : p)); setMeasuredBodyH(0); return; }
    // getBoundingClientRect, NOT offsetTop/offsetHeight. offset* round to whole
    // pixels, so a wrapped two-line label that really stands 42.8px tall reports
    // 43 — and the canvas bands, driven off that 43, gain 0.2px on the label
    // every row. Invisible at the top; by row 135 the bar sits a half-row below
    // its own title. The rects are fractional, so the bands match the labels to
    // the sub-pixel and the two columns stay locked however long the list runs
    // (interaction-witness, 2 Nov 2026). One read per row, no interleaved writes.
    const rects = kids.map((k) => k.getBoundingClientRect());
    const base = rects[0].top;
    const t = rects.map((r) => r.top - base);
    const h = rects.map((r) => r.height);
    setTops((p) => (eqArr(p, t) ? p : t));
    setHeights((p) => (eqArr(p, h) ? p : h));
    const bh = t[t.length - 1] + h[h.length - 1];
    setMeasuredBodyH((p) => (p === bh ? p : bh));
  }, [rows, canvasAvail, labelW]);
  // Vertical positions, measured when available, provisional (one line per row) before the
  // first measure. A bar CENTRES in its row, so it stays aligned when the row grows.
  const rowHOf = (i: number) => heights[i] ?? (rows[i]?.kind === 'group' ? GROUP_H : ROW_H);
  const topOf = (i: number) => tops[i] ?? rows[i]?.top ?? 0;
  const barTopOf = (i: number) => topOf(i) + (rowHOf(i) - BAR_H) / 2;
  const effBodyH = measuredBodyH || bodyH;

  // HOVER A CARD → light its whole dependency chain, dim the rest. The connected set
  // is the card plus every ancestor and descendant reached through the links.
  const [hoverRef, setHoverRef] = useState<string | null>(null);
  const chainSet = useMemo(() => {
    if (!hoverRef) return null;
    const seen = new Set<string>([hoverRef]);
    const walk = (ref: string, adj: Map<string, string[]>) => {
      for (const nx of adj.get(ref) ?? []) if (!seen.has(nx)) { seen.add(nx); walk(nx, adj); }
    };
    walk(hoverRef, predOf);
    walk(hoverRef, succOf);
    return seen;
  }, [hoverRef, predOf, succOf]);

  const who = (c: Issue): string => (hasDay(c.assignee) ? initials(displayName(c.assignee, profiles)) : '—');

  const barClass = (card: Issue, node: CpmNode | undefined): string => {
    const cls = ['g-bar'];
    const notStarted = card.status === 'todo' || card.status === 'backlog';
    if (card.status === 'done') cls.push('s-done');
    else if (node?.critical) cls.push('crit', notStarted ? 's-todo-c' : '');
    else if (node?.nearCritical) cls.push('near');
    else if (card.status === 'inprogress' || card.status === 'staging') cls.push('s-prog');
    else cls.push('s-todo');
    return cls.filter(Boolean).join(' ');
  };
  const isOverdue = (c: Issue) => !isResolved(c.status) && hasDay(c.dueDate) && c.dueDate! < todayISO;

  // ── THE MONTH GRID — the light near-term lens: weeks as rows, 7 day columns, each
  // card a bar across the days it runs. Status-only (no critical path, float or
  // baseline — those live on the Timeline), so the two views never compete. ───────
  const monthModel = useMemo(() => {
    const { y, m } = monthAnchor;
    const firstDow = new Date(y, m, 1).getDay(); // 0 Sun .. 6 Sat
    const toMonday = (firstDow + 6) % 7;
    const gridStart = new Date(y, m, 1 - toMonday);
    const lastDate = new Date(y, m + 1, 0).getDate();
    const weekCount = Math.ceil((toMonday + lastDate) / 7);

    const placeable = issues.filter((i) => i.status !== 'wontdo' && hasDay(i.dueDate));
    const weeks = Array.from({ length: weekCount }, (_, w) => {
      const wkStartMs = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + w * 7).getTime();
      const wkStartISO = toISO(wkStartMs);
      const wkEndISO = toISO(wkStartMs + 6 * DAY);
      const days = Array.from({ length: 7 }, (_, d) => {
        const iso = toISO(wkStartMs + d * DAY);
        const dd = new Date(wkStartMs + d * DAY);
        const hol = holidayOn(iso, workCalendar);
        return { iso, dn: dd.getDate(), other: dd.getMonth() !== m, today: iso === todayISO, weekend: dd.getDay() === 0 || dd.getDay() === 6, holiday: !!hol, holName: hol };
      });
      const over = placeable
        .filter((c) => {
          const s = hasDay(c.startsOn) ? c.startsOn! : c.dueDate!;
          return s <= wkEndISO && c.dueDate! >= wkStartISO;
        })
        .map((c) => {
          const s = hasDay(c.startsOn) ? c.startsOn! : c.dueDate!;
          const startCol = Math.max(0, daysBetween(wkStartISO, s < wkStartISO ? wkStartISO : s));
          const endCol = Math.min(6, daysBetween(wkStartISO, c.dueDate! > wkEndISO ? wkEndISO : c.dueDate!));
          return { card: c, startCol, endCol, contL: s < wkStartISO, contR: c.dueDate! > wkEndISO };
        })
        .sort((a, b) => a.startCol - b.startCol || (a.card.dueDate! < b.card.dueDate! ? -1 : 1));
      const laneEnd: number[] = [];
      const bars = over.map((o) => {
        let lane = laneEnd.findIndex((e) => e < o.startCol);
        if (lane === -1) { lane = laneEnd.length; laneEnd.push(o.endCol); } else laneEnd[lane] = o.endCol;
        return { ...o, lane };
      });
      // A week grows to a comfortable height; any lanes past the cap collapse behind a
      // "+N more" that expands the week inline (Apple/Google-style). Nothing is ever
      // clipped away — it is one click from view — and no date is covered (Vinnie, 31 Oct).
      const more = bars.filter((b) => b.lane >= MONTH_LANE_CAP).length;
      return { days, bars, lanes: laneEnd.length, more, key: wkStartISO };
    });
    const label = `${new Date(y, m, 1).toLocaleString('en-GB', { month: 'long' })} ${y}`;
    return { weeks, label };
  }, [issues, workCalendar, monthAnchor, todayISO]);

  const stepMonth = (delta: number) => setMonthAnchor((a) => {
    const t = a.m + delta;
    return { y: a.y + Math.floor(t / 12), m: ((t % 12) + 12) % 12 };
  });
  const mBarClass = (b: { card: Issue; contL: boolean; contR: boolean }) => {
    const c = b.card;
    const st = c.status === 'done' ? 's-done' : c.status === 'inprogress' || c.status === 'staging' ? 's-prog' : 's-todo';
    return ['mcal-bar', st, b.contL ? 'cont-l' : '', b.contR ? 'cont-r' : ''].filter(Boolean).join(' ');
  };
  const mBarStyle = (b: { startCol: number; endCol: number; contL: boolean; contR: boolean; lane: number }): CSSProperties => {
    const L = (b.startCol / 7) * 100;
    const W = ((b.endCol - b.startCol + 1) / 7) * 100;
    const left = b.contL ? `${L}%` : `calc(${L}% + 3px)`;
    const width = b.contL || b.contR ? `calc(${W}% - 3px)` : `calc(${W}% - 6px)`;
    return { left, width, top: b.lane * MONTH_LANE_H };
  };
  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const foreLabel = forecastISO ? asDate(localMs(forecastISO)) : null;
  const golLabel = goLiveISO ? asDate(localMs(goLiveISO)) : null;

  return (
    <div className="calview" ref={calRef}>
      {/* VIEW / GROUP / ZOOM controls (Board⇄Grid⇄Calendar lives in the board header).
          Inside Calendar, Timeline ↔ Month; the Timeline keeps Group + Zoom, the
          Month keeps a month stepper. */}
      <div className="cal-bar">
        {/* The Timeline ↔ Month switch is the project's choice; a continuous board
            has only Month, so it shows no switch at all (and reads the month title
            on the right instead). */}
        {isProject && (
          <>
            <span className="cal-sub-lab">View</span>
            <div className="seg">
              <button className={`seg-btn${view === 'timeline' ? ' active' : ''}`} aria-pressed={view === 'timeline'} onClick={() => setView('timeline')}>Timeline</button>
              <button className={`seg-btn${view === 'month' ? ' active' : ''}`} aria-pressed={view === 'month'} onClick={() => setView('month')}>Month</button>
            </div>
            <div className="sep" />
          </>
        )}
        {view === 'timeline' && isProject ? (
          <>
            <span className="cal-sub-lab">Group</span>
            <div className="seg">
              <button className={`seg-btn${group === 'phase' ? ' active' : ''}`} aria-pressed={group === 'phase'} onClick={() => setGroup('phase')}>Phase</button>
              <button className={`seg-btn${group === 'epic' ? ' active' : ''}`} aria-pressed={group === 'epic'} onClick={() => setGroup('epic')}>Epic</button>
            </div>
            <div className="cal-right">
              <span className="cal-sub-lab">Zoom</span>
              <div className="seg zoom-seg">
                <button className={`seg-btn${zoom === 'day' ? ' active' : ''}`} aria-pressed={zoom === 'day'} onClick={() => setZoomOverride('day')}>Day</button>
                <button className={`seg-btn${zoom === 'week' ? ' active' : ''}`} aria-pressed={zoom === 'week'} onClick={() => setZoomOverride('week')}>Week</button>
                <button className={`seg-btn${zoom === 'month' ? ' active' : ''}`} aria-pressed={zoom === 'month'} onClick={() => setZoomOverride('month')}>Month</button>
              </div>
            </div>
          </>
        ) : (
          <div className="cal-right mcal-nav">
            <button type="button" className="btn btn-ghost mcal-step" aria-label="Previous month" onClick={() => stepMonth(-1)}>‹</button>
            <b className="mcal-title">{monthModel.label}</b>
            <button type="button" className="btn btn-ghost mcal-step" aria-label="Next month" onClick={() => stepMonth(1)}>›</button>
          </div>
        )}
      </div>

      {view === 'month' || !isProject ? (
        <div className="mcal">
          <div className="mcal-dow">
            {DOW.map((d, i) => <div key={d} className={i >= 5 ? 'we' : ''}>{d}</div>)}
          </div>
          {monthModel.weeks.map((wk, wi) => {
            const hasMore = wk.more > 0;
            const open = openWeeks.has(wk.key);
            const shownLanes = hasMore && !open ? MONTH_LANE_CAP : wk.lanes;
            const barsH = Math.max(1, shownLanes) * MONTH_LANE_H;
            // Reserve a row for the "+N more" / "Show less" chip when the week overflows.
            const weekH = Math.max(76, 30 + barsH + (hasMore ? 26 : 4));
            return (
            <div className="mcal-week" key={wi} style={{ minHeight: weekH }}>
              {wk.days.map((d, di) => (
                <div key={di} className={['mcal-day', d.other ? 'oth' : '', d.today ? 'today' : '', d.weekend ? 'we' : '', d.holiday ? 'hol' : ''].filter(Boolean).join(' ')}>
                  <span className="dn">{d.dn}</span>
                  {d.holiday && d.holName && <div className="holn">{d.holName}</div>}
                </div>
              ))}
              {/* Every bar is rendered; when collapsed, the lanes past the cap are simply
                  clipped by the shorter overlay, so expanding reveals them with a glide
                  rather than a pop (R32). */}
              <div className="mcal-bars" style={{ height: barsH }}>
                {wk.bars.map((b) => {
                  const c = b.card;
                  const pct = effectiveProgressPct(c);
                  return (
                    <div key={c.id} className={mBarClass(b)} style={mBarStyle(b)} title={c.title}
                      role="button" tabIndex={0} onClick={() => openIssue(c.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIssue(c.id); } }}>
                      {c.title} · {who(c)}{pct != null && c.status !== 'done' ? ` · ${pct}%` : ''}
                      {/* (UX-M4) The one canonical overdue badge (R3 .rag, borderless, tinted,
                          Title-Case) — the same mark Needs Attention and the board card use, not
                          an ad-hoc lowercase word. */}
                      {isOverdue(c) && <span className="rag r cal-od"><span className="dot" />Overdue</span>}
                    </div>
                  );
                })}
              </div>
              {hasMore && (
                <button type="button" className="mcal-more" style={{ top: 30 + barsH + 3 }}
                  onClick={() => toggleWeek(wk.key)}
                  aria-expanded={open}>
                  {open ? 'Show less' : `+${wk.more} more`}
                </button>
              )}
            </div>
            );
          })}
        </div>
      ) : (
      <>
      <div className="gantt">
        <div className="gantt-inner">
          {/* LEFT: the card list — a fixed-width column OUTSIDE the horizontal scroller, so the
              task names never scroll off when you reach for a later date. */}
          <div className="g-labels" ref={labelsRef} style={{ width: labelW }}>
            {/* Drag the boundary (or focus it and use ← →) to trade canvas width for more
                title room; remembered per board. */}
            <div className="g-split" tabIndex={0} onPointerDown={onSplitDown} onPointerMove={onSplitMove} onPointerUp={onSplitUp}
              onKeyDown={onSplitKey} role="separator" aria-orientation="vertical"
              aria-valuenow={Math.round(labelW)} aria-valuemin={160} aria-valuemax={560}
              aria-label="Resize the task column" title="Drag, or focus and press ← → , to resize the task column" />
            <div className="g-lh">Work · {model.scheduledCount} of {model.totalCount} scheduled</div>
            {rows.map((r, i) =>
              r.kind === 'group' ? (
                <div className="g-group" key={`g${i}`} title={r.name}>
                  <span className="nm">{r.name}</span>
                  <span className="cnt"><b>{r.count}</b> · {r.note}</span>
                </div>
              ) : (
                <div className="g-lrow" key={r.card.id} title={r.card.title} onClick={() => openIssue(r.card.id)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIssue(r.card.id); } }}>
                  {r.node?.critical && <span className="cflag"><span className="di" /></span>}
                  <span className="tt">{r.card.title}</span>
                  <span className="who">{who(r.card)}</span>
                </div>
              ),
            )}
          </div>

          {/* RIGHT: the dated canvas — the only thing that scrolls sideways */}
          <div className="gantt-scroll">
          <div className="g-canvas" style={{ width }}>
            <div className="g-axis">
              {months.map((m, i) => <span className="mon" key={`m${i}`} style={{ left: m.left }}>{m.label}</span>)}
              {weeks.map((w, i) => <span className="wk" key={`w${i}`} style={{ left: w.left }}>{w.label}</span>)}
            </div>

            <div className="g-body" style={{ height: effBodyH }}>
              {/* weekend / holiday bands */}
              {bands.map((b, i) =>
                b.holiday
                  ? <div className="g-hol" key={`b${i}`} style={{ left: b.left, '--d': `${b.width}px` } as CSSProperties}><span className="tag">★</span></div>
                  : <div className="g-wknd" key={`b${i}`} style={{ left: b.left, width: b.width }} />,
              )}

              {/* row background bands (mirror the label rows for alignment) */}
              {rows.map((r, i) => <div className={r.kind === 'group' ? 'g-grpband' : 'g-rowband'} key={`rb${i}`} style={{ height: rowHOf(i) }} />)}

              {/* vertical reference lines + chips */}
              <div className="g-today" style={{ left: x(todayISO) }} />
              {goLiveISO && <div className="g-vline golive" style={{ left: x(goLiveISO) }} />}
              {forecastISO && <div className="g-vline fore" style={{ left: x(forecastISO) }} />}
              <div className="g-lab today" style={{ left: x(todayISO) + 4, top: 5 }}>Today · {asDate(now)}</div>
              {golLabel && <div className="g-lab golive" style={{ left: x(goLiveISO!) - 128, top: 5 }}>Plan go-live · {golLabel}</div>}
              {foreLabel && <div className="g-lab fore" style={{ left: x(forecastISO!) + 4, top: 30 }}>Forecast · {foreLabel}</div>}

              {/* float tails — the slack between a card's due date and its latest finish */}
              {floats.map((f, i) => <div className="g-float" key={`f${i}`} title={`${f.slack} working ${f.slack === 1 ? 'day' : 'days'} of slack — room to slip without moving the finish`} style={{ left: f.left, width: f.width, top: barTopOf(f.row) }} />)}

              {/* DEPENDENCY LINKS — a smooth connector with a node at each end,
                  finish→start. No arrowheads: each curve carries a gradient that
                  brightens toward the dependent card, so direction reads without one.
                  Colour is conditional on the predecessor — petrol on the critical
                  chain, amber where an overdue card is holding up the next, green once
                  cleared, grey otherwise. The critical chain glows softly and a faint
                  light drifts along it. Hovering a card lights its whole chain. */}
              {links.length > 0 && (
                <svg className="g-links" width={width} height={effBodyH} aria-hidden="true">
                  <defs>
                    {links.map((l, i) => {
                      const col = LINK_STROKE[l.state];
                      const y1 = barTopOf(l.fromRow) + 10, y2 = barTopOf(l.toRow) + 10;
                      return (
                        <linearGradient key={i} id={`g-lg${i}`} gradientUnits="userSpaceOnUse" x1={l.x1} y1={y1} x2={l.x2} y2={y2}>
                          <stop offset="0" style={{ stopColor: col, stopOpacity: 0.2 }} />
                          <stop offset="1" style={{ stopColor: col, stopOpacity: 1 }} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  {links.map((l, i) => {
                    // horizontal control handles → the curve leaves the predecessor
                    // and arrives at the successor flowing sideways, a clean S. The y of
                    // each end is the centre of its bar, read off the measured row heights.
                    const y1 = barTopOf(l.fromRow) + 10, y2 = barTopOf(l.toRow) + 10;
                    const dx = Math.max(16, Math.min(42, Math.abs(l.x2 - l.x1) * 0.6));
                    const d = `M ${l.x1} ${y1} C ${l.x1 + dx} ${y1}, ${l.x2 - dx} ${y2}, ${l.x2} ${y2}`;
                    const hov = chainSet ? (chainSet.has(l.fromRef) && chainSet.has(l.toRef) ? ' hot' : ' dim') : '';
                    return (
                      <g key={i} className={`lk ${l.state}${hov}`}>
                        <path className="ln" d={d} stroke={`url(#g-lg${i})`} />
                        {l.state === 'critical' && <path className="flow" d={d} />}
                        <circle className="nd from" cx={l.x1} cy={y1} r={2.4} />
                        <circle className="nd to" cx={l.x2} cy={y2} r={3.2} />
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* bars + baselines */}
              {rows.map((r, i) => {
                if (r.kind !== 'card') return null;
                const c = r.card;
                const left = x(c.startsOn!);
                const w = Math.max(model.dayPx, x(c.dueDate!) + model.dayPx - left);
                const pct = effectiveProgressPct(c);
                const overdue = isOverdue(c);
                const dim = chainSet && !chainSet.has(c.ref) ? ' g-dim' : '';
                // Bar centres in its (possibly two-line) row; the dot and baseline follow it.
                const barTop = barTopOf(i);
                return (
                  <div key={`bar${c.id}`}>
                    <div className={`${barClass(c, r.node)}${dim}`} style={{ left, width: w, top: barTop }}
                      onClick={() => openIssue(c.id)} onMouseEnter={() => setHoverRef(c.ref)} onMouseLeave={() => setHoverRef(null)}
                      role="button" tabIndex={-1} title={c.title}>
                      {pct != null && c.status !== 'done' && <span className="fillp" style={{ width: `${pct}%` }} />}
                      <span className="pct">{c.status === 'done' ? '✓' : pct != null ? `${pct}%` : ''}</span>
                    </div>
                    {/* (UX-M4) A bar can't carry a pill, so overdue reads as the canonical-compact
                        RAG marker — a dot in the SAME red token as the .rag "Overdue" badge, with an
                        accessible label — not a bare "!" glyph. */}
                    {overdue && <div className="g-overdue" style={{ left: left + w - 8, top: barTop - 2 }} role="img" aria-label="Overdue" title="Overdue" />}
                    <div className={`g-base${overdue ? ' slip' : ''}`} style={{ left, width: w, top: barTop + 21 }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div></div>

        {/* COVERAGE + LEGEND */}
        <div className="g-foot">
          <div className="g-cov">
            {/* Lead with what IS true. A critical path is the longest CHAIN of dependent
                work, so with nothing linked there is no chain to find, however many cards
                are dated (pm-witness L193, 1 Nov) — say that, don't print "runs through 0
                cards" as if 0 were a reading. The undated note is a separate fact: those
                cards cannot sit on the timeline at all. */}
            {cpm.criticalPath.length > 0 ? (
              <>Critical path runs through <b>{cpm.criticalPath.length} {cpm.criticalPath.length === 1 ? 'card' : 'cards'}</b> · </>
            ) : links.length === 0 ? (
              <><span className="warn">Nothing is linked yet</span> — a critical path needs cards that depend on each other · </>
            ) : (
              <>No critical path yet · </>
            )}
            <b>{cpm.coverage.scheduled} of {cpm.coverage.total} cards</b> scheduled
            {undated.length > 0 && <> · <span className="warn">{undated.length} undated</span> can't be placed or counted</>}
          </div>
          <div className="g-legend">
            <span className="it"><span className="lk-s crit" />Critical path</span>
            <span className="it"><span className="lk-s risk" />Holding up the next</span>
            <span className="it"><span className="lk-s ok" />Cleared</span>
            <span className="it"><span className="lk-s" />Depends on</span>
            <span className="it"><span className="lg near" />Near-critical</span>
            <span className="it"><span className="g-float lgf" />Float</span>
            <span className="it"><span className="odot" />Overdue</span>
          </div>
        </div>
      </div>

      {/* UNSCHEDULED TRAY */}
      {undated.length > 0 && (
        <div className="g-tray">
          <div className="g-tray-h">
            <span className="ti">Unscheduled</span>
            <span className="sub"><b>{undated.length} {undated.length === 1 ? 'card' : 'cards'}</b> {undated.length === 1 ? 'has' : 'have'} no start or due date — set dates to place {undated.length === 1 ? 'it' : 'them'} on the timeline, then link the ones that depend on each other to form a critical path.</span>
          </div>
          <div className="g-tray-b">
            {undated.map((c) => (
              <span className="g-chip" key={c.id} onClick={() => openIssue(c.id)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIssue(c.id); } }}>
                {c.title}
                {c.moscow === 'must' && <span className="wtag">Must-have</span>}
                <span className="set">Set dates</span>
              </span>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
